/**
 * Tiny Web-Audio sound engine.
 *
 * All effects are synthesised on the fly (no audio files to load or ship), so
 * the game stays self-contained and works offline in the Android WebView. The
 * AudioContext is created lazily and resumed on the first user gesture to
 * satisfy browser autoplay policies.
 */

export type SoundName =
  | "tap"
  | "button"
  | "toggle"
  | "note"
  | "place"
  | "erase"
  | "undo"
  | "redo"
  | "hint"
  | "mistake"
  | "win";

interface ToneOpts {
  freq: number;
  dur: number;
  type?: OscillatorType;
  gain?: number;
  delay?: number;
  glideTo?: number;
  /** Random pitch wobble, in cents (±), so repeats never sound identical. */
  detune?: number;
}

/** Semitone offsets of a major pentatonic scale, for picking pleasant pitches. */
const PENTA = [0, 2, 4, 7, 9, 12];
const semi = (base: number, semitones: number): number => base * Math.pow(2, semitones / 12);
const pick = <T>(arr: readonly T[]): T => arr[(Math.random() * arr.length) | 0];
const jitter = (cents: number): number => (Math.random() * 2 - 1) * cents;

export class Sound {
  enabled = true;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;

  private ensure(): AudioContext | null {
    const Ctx =
      (globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
        .AudioContext ??
      (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return null;
    if (!this.ctx) {
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.32;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    return this.ctx;
  }

  /** Unlock/resume audio, safe to call repeatedly from user gestures. */
  unlock(): void {
    if (this.enabled) this.ensure();
  }

  private tone(o: ToneOpts): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const t0 = ctx.currentTime + (o.delay ?? 0);
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = o.type ?? "sine";
    osc.frequency.setValueAtTime(o.freq, t0);
    osc.detune.setValueAtTime(jitter(o.detune ?? 10), t0);
    if (o.glideTo) osc.frequency.exponentialRampToValueAtTime(o.glideTo, t0 + o.dur);
    const peak = o.gain ?? 0.5;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + o.dur);
    osc.connect(g);
    g.connect(master);
    osc.start(t0);
    osc.stop(t0 + o.dur + 0.03);
  }

  play(name: SoundName): void {
    if (!this.enabled || !this.ensure()) return;
    switch (name) {
      // Light taps hop around a pentatonic scale so quick repeats feel playful.
      case "tap":
        this.tone({ freq: semi(1320, pick(PENTA)), dur: 0.03, type: "sine", gain: 0.14, detune: 18 });
        break;
      case "button":
        this.tone({ freq: semi(880, pick([0, 2, 5])), dur: 0.035, type: "triangle", gain: 0.16 });
        break;
      case "toggle":
        this.tone({ freq: semi(620, pick([0, 3])), dur: 0.045, type: "triangle", gain: 0.18 });
        break;
      case "note":
        this.tone({ freq: semi(1040, pick(PENTA)), dur: 0.035, type: "triangle", gain: 0.14, detune: 18 });
        break;
      case "place": {
        // Random root + a rising fifth, each placement is a small, fresh chirp.
        const root = semi(523, pick([0, 2, 4, 5, 7]));
        this.tone({ freq: root, dur: 0.12, type: "sine", gain: 0.38, glideTo: root * 1.5 });
        break;
      }
      case "erase":
        this.tone({ freq: semi(440, pick([0, -2])), dur: 0.1, type: "sine", gain: 0.26, glideTo: 220 });
        break;
      case "undo":
        this.tone({ freq: 420, dur: 0.09, type: "triangle", gain: 0.24, glideTo: 300 });
        break;
      case "redo":
        this.tone({ freq: 420, dur: 0.09, type: "triangle", gain: 0.24, glideTo: 600 });
        break;
      case "hint": {
        const base = pick([699, 740, 784]);
        this.tone({ freq: base, dur: 0.1, type: "sine", gain: 0.28 });
        this.tone({ freq: base * 1.5, dur: 0.13, type: "sine", gain: 0.28, delay: 0.09 });
        break;
      }
      case "mistake":
        this.tone({ freq: semi(180, pick([0, -1, 1])), dur: 0.18, type: "sawtooth", gain: 0.2, glideTo: 120 });
        break;
      case "win": {
        // One of a few celebratory runs, so winning doesn't always sound the same.
        const runs = [
          [523, 659, 784, 1047, 1319],
          [587, 740, 880, 1175, 1397],
          [523, 698, 784, 1047, 1568],
        ];
        pick(runs).forEach((f, i) =>
          this.tone({ freq: f, dur: 0.28, type: "sine", gain: 0.3, delay: i * 0.12 }),
        );
        break;
      }
    }
  }

  /**
   * Rising note for the n-th cell resolved in an auto-solve cascade, so the
   * chain reaction sounds like an ascending run.
   */
  cascade(index: number): void {
    if (!this.enabled || !this.ensure()) return;
    const penta = [0, 2, 4, 7, 9];
    const semis = penta[index % penta.length] + 12 * Math.floor(index / penta.length);
    const freq = 392 * Math.pow(2, Math.min(semis, 33) / 12);
    this.tone({ freq, dur: 0.11, type: "sine", gain: 0.3 });
  }
}

export const sound = new Sound();
